/**
 * sheetScale — shared geometry service for the Distance/Area/Radius/
 * Diameter/Angle/Arc measure tools. Mirrors the canonical server
 * implementation in backend/services/units.py EXACTLY so the on-canvas
 * live preview and the server-persisted value never disagree.
 *
 * HIGH fix (real-plan cert iteration_183) — coordinate model:
 * Normalized sheet coordinates (nx, ny) are NOT isotropic. One
 * normalized unit of nx spans `raster_width_px` pixels; one normalized
 * unit of ny spans `raster_height_px` pixels. On any non-square sheet
 * those pixel counts differ (a 2400x1696 raster measured a vertical
 * segment +41.5% too long under the old model), so ALL geometry below
 * converts to true pixel space (pixel_x = nx * raster_width_px,
 * pixel_y = ny * raster_height_px) BEFORE any distance/area/angle/
 * circle-fit math — never operate on raw (nx, ny) directly.
 *
 * `scale` objects come from GET/PUT /sheets/{id}/scale and carry
 * raster_width_px / raster_height_px, established server-side from the
 * canonical raster (never client-supplied). `angle` doesn't need a real
 * scale but still needs those dims — pass the viewer's `imgDims`
 * ({ w, h }) directly. Every function returns null when dims aren't
 * available rather than silently assuming square pixels.
 */

function _toPx(dims, p) {
  if (!dims || p == null) return null;
  const w = dims.raster_width_px ?? dims.w;
  const h = dims.raster_height_px ?? dims.h;
  if (!w || !h) return null;
  return { x: p.nx * w, y: p.ny * h };
}

function _baselinePx(scale) {
  if (!scale || scale.p1_nx == null || scale.p2_nx == null) return null;
  const p1 = _toPx(scale, { nx: scale.p1_nx, ny: scale.p1_ny });
  const p2 = _toPx(scale, { nx: scale.p2_nx, ny: scale.p2_ny });
  if (!p1 || !p2) return null;
  const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  return len > 0 ? len : null;
}

function _ftPerPx(scale) {
  const base = _baselinePx(scale);
  if (!base || !scale?.real_distance_ft) return null;
  return scale.real_distance_ft / base;
}

export function isCalibrated(scale) {
  return _baselinePx(scale) != null && !!scale?.real_distance_ft;
}

export function distanceFt(scale, a, b) {
  const ftPerPx = _ftPerPx(scale);
  const pa = _toPx(scale, a);
  const pb = _toPx(scale, b);
  if (!ftPerPx || !pa || !pb) return null;
  return Math.hypot(pb.x - pa.x, pb.y - pa.y) * ftPerPx;
}

export function polylineLengthFt(scale, points) {
  if (!points || points.length < 2) return null;
  const ftPerPx = _ftPerPx(scale);
  if (!ftPerPx) return null;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const a = _toPx(scale, points[i - 1]);
    const b = _toPx(scale, points[i]);
    if (!a || !b) return null;
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total * ftPerPx;
}

export function polygonAreaSf(scale, verts) {
  if (!verts || verts.length < 3) return null;
  const ftPerPx = _ftPerPx(scale);
  if (!ftPerPx) return null;
  const pxPts = verts.map((v) => _toPx(scale, v));
  if (pxPts.some((p) => !p)) return null;
  const n = pxPts.length;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const a = pxPts[i];
    const b = pxPts[(i + 1) % n];
    sum += a.x * b.y - b.x * a.y;
  }
  const areaPx = Math.abs(sum) / 2;
  return areaPx * ftPerPx * ftPerPx;
}

// `angle` needs no real-world scale (no real_distance_ft) but DOES need
// the raster's pixel width/height — `dims` is either a `scale` object or
// the viewer's plain `imgDims` ({ w, h }).
export function angleDegrees(points, dims) {
  if (!points || points.length !== 3) return null;
  const [a, b, c] = points;
  const pa = _toPx(dims, a);
  const pb = _toPx(dims, b);
  const pc = _toPx(dims, c);
  if (!pa || !pb || !pc) return null;
  const v1 = { x: pa.x - pb.x, y: pa.y - pb.y };
  const v2 = { x: pc.x - pb.x, y: pc.y - pb.y };
  const m1 = Math.hypot(v1.x, v1.y);
  const m2 = Math.hypot(v2.x, v2.y);
  if (m1 === 0 || m2 === 0) return null;
  let cosA = (v1.x * v2.x + v1.y * v2.y) / (m1 * m2);
  cosA = Math.max(-1, Math.min(1, cosA));
  return (Math.acos(cosA) * 180) / Math.PI;
}

// Least-squares circle fit (Kasa method) in real-feet space, then arc
// length from the sampled points' angular span around that center.
function _fitCircleFt(ptsFt) {
  const n = ptsFt.length;
  if (n < 3) return null;
  let sumX = 0, sumY = 0, sumX2 = 0, sumY2 = 0, sumXY = 0, sumX3 = 0, sumY3 = 0, sumXY2 = 0, sumX2Y = 0;
  for (const p of ptsFt) {
    const { x, y } = p;
    sumX += x; sumY += y;
    sumX2 += x * x; sumY2 += y * y; sumXY += x * y;
    sumX3 += x * x * x; sumY3 += y * y * y;
    sumXY2 += x * y * y; sumX2Y += x * x * y;
  }
  const C = n * sumX2 - sumX * sumX;
  const D = n * sumXY - sumX * sumY;
  const G = n * sumY2 - sumY * sumY;
  const denom = C * G - D * D;
  if (Math.abs(denom) < 1e-12) return null;
  // HIGH fix (real-plan cert iteration_184) — E and H are Kasa moment
  // terms and MUST carry the 0.5 factor from the normal equations; without
  // it the fitted centre comes out at exactly 2x the true centre (a
  // real-plan semicircle read 73ft instead of the correct 151.9ft).
  const E = 0.5 * (n * sumX3 + n * sumXY2 - (sumX2 + sumY2) * sumX);
  const H = 0.5 * (n * sumX2Y + n * sumY3 - (sumX2 + sumY2) * sumY);
  const cx = (E * G - H * D) / denom;
  const cy = (H * C - E * D) / denom;
  let r = 0;
  for (const p of ptsFt) r += Math.hypot(p.x - cx, p.y - cy);
  r /= n;
  return { cx, cy, r };
}

export function arcLengthFt(scale, points) {
  const ftPerPx = _ftPerPx(scale);
  if (!ftPerPx || !points || points.length < 3) return null;
  const pxPts = points.map((p) => _toPx(scale, p));
  if (pxPts.some((p) => !p)) return null;
  const ptsFt = pxPts.map((p) => ({ x: p.x * ftPerPx, y: p.y * ftPerPx }));
  const fit = _fitCircleFt(ptsFt);
  if (!fit || !Number.isFinite(fit.r) || fit.r <= 0) return null;
  const angles = ptsFt.map((p) => Math.atan2(p.y - fit.cy, p.x - fit.cx));
  // Unwrap so the sampled angles form a continuous, monotonic sweep.
  const unwrapped = [angles[0]];
  for (let i = 1; i < angles.length; i++) {
    let d = angles[i] - unwrapped[i - 1];
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    unwrapped.push(unwrapped[i - 1] + d);
  }
  const totalAngle = Math.abs(unwrapped[unwrapped.length - 1] - unwrapped[0]);
  if (totalAngle < 1e-9) return null;
  return fit.r * totalAngle;
}

/**
 * Format a feet value as a clean operational label. Examples:
 *   12.4 → "12.4'"
 *   245.0 → "245'"
 *   2350 → "2,350' (0.44 mi)"
 *   0.83 → "10\""  (sub-foot → inches)
 *
 * Field-natural, no engineering decimal clutter.
 */
export function formatFeet(ft) {
  if (ft == null || !Number.isFinite(ft)) return "—";
  const abs = Math.abs(ft);
  if (abs < 1) {
    const inches = Math.round(ft * 12);
    return `${inches}"`;
  }
  if (abs < 10) return `${ft.toFixed(1)}'`;
  if (abs < 1000) return `${Math.round(ft)}'`;
  if (abs < 5280) {
    const k = ft >= 0 ? 1 : -1;
    return `${k * Math.round(abs).toLocaleString()}'`;
  }
  const mi = ft / 5280;
  return `${Math.round(ft).toLocaleString()}' (${mi.toFixed(2)} mi)`;
}

/**
 * Format an area in square feet with operational units (SF / SY / acres).
 */
export function formatArea(sf) {
  if (sf == null || !Number.isFinite(sf)) return "—";
  if (sf < 1000) return `${Math.round(sf)} SF`;
  const sy = sf / 9;
  if (sf < 43560) return `${Math.round(sf).toLocaleString()} SF · ${Math.round(sy).toLocaleString()} SY`;
  const ac = sf / 43560;
  return `${Math.round(sf).toLocaleString()} SF · ${ac.toFixed(2)} ac`;
}
